import React, { useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Clock } from 'lucide-react';
import { useTestStore } from '../../store/testStore';
import ProgressBar from '../common/ProgressBar';

interface TimerProps {
  onTimeEnd?: () => void;
}

const Timer: React.FC<TimerProps> = ({ onTimeEnd }) => {
  const { timeRemaining, decrementTimer, isTestStarted, isTestCompleted } = useTestStore();
  
  const formatTime = useCallback((seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
  }, []);
  
  useEffect(() => {
    if (!isTestStarted || isTestCompleted || timeRemaining <= 0) return;
    
    const timer = setInterval(() => {
      decrementTimer();
    }, 1000);
    
    return () => clearInterval(timer);
  }, [isTestStarted, isTestCompleted, timeRemaining, decrementTimer]);
  
  useEffect(() => {
    if (timeRemaining === 0 && onTimeEnd) {
      onTimeEnd();
    }
  }, [timeRemaining, onTimeEnd]);
  
  // Calculate percentage of time remaining
  const totalTime = 60 * 60; // 60 minutes in seconds
  const percentageRemaining = (timeRemaining / totalTime) * 100;
  
  // Determine color based on time remaining
  let timerColor = 'success';
  if (percentageRemaining < 50) timerColor = 'warning';
  if (percentageRemaining < 25) timerColor = 'error';
  
  return (
    <motion.div 
      className="bg-white rounded-lg shadow-card p-4"
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
    >
      <div className="flex items-center mb-2">
        <Clock className="w-5 h-5 text-gray-600 mr-2" />
        <h3 className="font-medium text-gray-800">Time Remaining</h3>
      </div>
      
      <div className="flex justify-between items-center mb-2">
        <motion.div 
          className={`text-2xl font-bold ${
            percentageRemaining < 25 ? 'text-error-600' : 
            percentageRemaining < 50 ? 'text-warning-500' : 
            'text-success-600'
          }`}
          key={timeRemaining}
          initial={{ scale: 1.2 }}
          animate={{ scale: 1 }}
          transition={{ duration: 0.2 }}
        >
          {formatTime(timeRemaining)}
        </motion.div>
        
        <span className="text-sm text-gray-500">60:00</span>
      </div>
      
      <ProgressBar 
        value={timeRemaining} 
        max={totalTime} 
        color={timerColor as 'success' | 'warning' | 'error'} 
      />
    </motion.div>
  );
};

export default Timer;